import {
  Account, AccountInfoItem,
  AccountSummaryResponse,
  ListBankTransactionsResponse, ListCashfreeTransactionsResponse,
  PSPType,
  PayoutUser,
  Split,
  TriggerRefundResponse,
  WalletTransactionsResponse,
} from './types';
import {Http} from './NetworkHelper';
import {getStorageItem, putStorageItem, removeStorageItem, StorageKeys} from './StorageHelper';
import {AxiosRequestConfig} from 'axios';

const apiClient = Http.createPayoutApiClient();

export const loadAccount = async (force?: boolean): Promise<Account> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const cacheBuster = force === true ? `?_r=${new Date().getTime()}` : '';
  const res = await apiClient.get<any, {data: {data: Account}}>(`/ledger/accounts/${accountId}${cacheBuster}`);
  return res.data.data;
};

export const fetchSummary = async (force?: boolean): Promise<AccountSummaryResponse> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const cacheBuster = force === true ? `?_r=${new Date().getTime()}` : '';
  const res = await apiClient.get<
    any,
    {
      data: {data: AccountSummaryResponse};
    }
  >(`/ledger/accounts/${accountId}/summary${cacheBuster}`);
  return res.data.data;
};

export const searchAccounts = async (query: string): Promise<any> => {
  const res = await apiClient.post<any, {data: {data: any}}>(`/ledger/accounts/search`, {query});
  return res.data.data;
};

export const listWalletTransactions = async (startDate: Date, endDate: Date, pspType?: PSPType): Promise<WalletTransactionsResponse> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const searchQuery = {
    accountId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    transactionCount: 1000,
    pspType
  };
  const res = await apiClient.post<any, {data: {data: any}}>(`/ledger/accounts/wallets/statement`, searchQuery);
  return res.data.data;
};

export const listBankTransactions = async (startDate: Date, endDate: Date, pspType?: PSPType): Promise<ListBankTransactionsResponse> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const searchQuery = {
    accountId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    transactionCount: 1000,
    pspType,
  };
  const res = await apiClient.post<any, {data: {data: any}}>(`/ledger/accounts/bank-transactions/statement`, searchQuery);
  return res.data.data;
};

export const listCashfreeTransactions = async (startDate: Date, endDate: Date, includeSucess = false, pspType?: PSPType): Promise<ListCashfreeTransactionsResponse> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const searchQuery = {
    requesterAccountId: accountId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    transactionCount: 1000,
    excludeSuccess: !includeSucess,
    pspType
  };
  const res = await apiClient.post<any, {data: {data: any}}>(`/transfer/cashfree/transactions`, searchQuery);
  return res.data.data;
};

export const listActiveAccounts = async (query?: string): Promise<AccountInfoItem[]> => {
  const res = await apiClient.get<any, {data: {data: any}}>(`/ledger/accounts`, {params: query ? {q: query} : {}});
  return res.data.data;
};

export const downloadAccountStatement = async (startDate: Date, endDate: Date): Promise<any> => {
  const accountId = await getStorageItem(StorageKeys.ACTIVE_ACCOUNT_ID);
  const searchQuery = {
    accountId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
  const res = await apiClient.post<any, {data: {url: any}}>(`/ledger/accounts/wallets/statement/pdf`, searchQuery);
  return res.data.url;
};

export const getUser = async (): Promise<PayoutUser> => {
  try {
    const res = await apiClient.get<any, {data: {data: PayoutUser}}>(`/auth/profile`);
    return res.data.data;
  } catch (err: any) {
    await removeStorageItem(StorageKeys.AUTH_JWT_ACCESS_TOKEN);
    throw err;
  }
};

export const login = async (username: string, password: string): Promise<PayoutUser> => {
  const res = await apiClient.post<any, any>(`/auth/login`, {username, password});
  await putStorageItem(StorageKeys.AUTH_JWT_ACCESS_TOKEN, res.data.access_token);
  return res.data.data;
};


export const postRequestWithApi = async(api:string, formData: any,  config:AxiosRequestConfig) => {
  const res = await apiClient.post<any, any>(api, formData, config);
  return res;
}

export const listPaymentBatches = async (excel = false) => {
  return apiClient.get(`/payout/list?excel=${excel}`);
};

export const generatePaymentBatches = async (excel = false) => {
  return apiClient.post(`/payout/generate?excel=${excel}`);
};

export const triggerPayouts = async () => {
  return apiClient.post(`/payout/trigger`);
};

export const listAmountCollected = async (search?:string, startDate?: string, endDate?:string) => {
  const data: any = {};
  if(search) data.search = search;
  if(startDate) data.startDate = new Date(startDate).toISOString();
  if(endDate) data.endDate = new Date(endDate).toISOString();
  return apiClient.post(`/auto-collect/get-amount-collected`, data);
}

export const listAmountCollectedRecon = async (amount_collected_id: string) => {
  return apiClient.get(`/auto-collect/get-amount-collected/${amount_collected_id}`);
}

export const splitAmountCollected = async (amount_collected_id: string, splits: Split[]) => {
  return apiClient.post(`/auto-collect/split-amount-collected/${amount_collected_id}`, splits.map(split => ({ vendorId: split.cashfreeVendorId, amount: Number(split.amount) })));
}

export const loadAccountById = async (accountId: string): Promise<Account> => {
  const res = await apiClient.get<any, {data: {data: Account}}>(`/ledger/accounts/${accountId}?_r=${new Date().getTime()}`);
  return res.data.data;
};

export const triggerRefund = async (accountId: string): Promise<TriggerRefundResponse> => {
  const res = await apiClient.post<any, {data: {data: TriggerRefundResponse}}>(`/refund/trigger`, {accountId});
  return res.data.data;
};